/**
 * ColorPicker Component
 * Color selection with visual preview and hex input
 */

import * as React from 'react';
import { cn } from '../../../lib/utils';
import { normalizeHexColor } from '../../../schemas/widgetConfigSchema';

export interface ColorPickerProps {
  value: string;
  onChange: (color: string) => void;
  label?: string;
  presets?: string[];
  error?: string;
  className?: string;
}

const DEFAULT_PRESETS = [
  '#3b82f6', // Primary blue
  '#2563eb',
  '#1d4ed8',
  '#0066cc',
  '#0052a3',
  '#4c1d95',
  '#7c2d12',
  '#991b1b',
  '#1f2937',
  '#374151',
];

/**
 * ColorPicker allows users to select colors via:
 * - Visual color picker input
 * - Hex code text input
 * - Preset color swatches
 */
export function ColorPicker({
  value,
  onChange,
  label,
  presets = DEFAULT_PRESETS,
  error,
  className,
}: ColorPickerProps) {
  const [localValue, setLocalValue] = React.useState(value);
  const [isValidHex, setIsValidHex] = React.useState(true);

  // Sync local value with prop value
  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleColorChange = (newColor: string) => {
    setLocalValue(newColor);

    // Validate hex format
    const normalized = normalizeHexColor(newColor);
    const isValid = /^#[0-9A-Fa-f]{6}$/.test(normalized);
    setIsValidHex(isValid);

    if (isValid) {
      onChange(normalized);
    }
  };

  const handlePresetClick = (preset: string) => {
    setLocalValue(preset);
    setIsValidHex(true);
    onChange(preset);
  };

  return (
    <div className={cn('space-y-3', className)}>
      {label && (
        <label className="block text-sm font-medium text-gray-900">
          {label}
        </label>
      )}

      {/* Color Picker and Preview */}
      <div className="flex items-center gap-3">
        {/* Visual Color Picker */}
        <div className="relative">
          <input
            type="color"
            value={localValue}
            onChange={(e) => handleColorChange(e.target.value)}
            className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
            aria-label={label || 'Color picker'}
          />
        </div>

        {/* Hex Input */}
        <div className="flex-1">
          <input
            type="text"
            value={localValue}
            onChange={(e) => handleColorChange(e.target.value)}
            placeholder="#3b82f6"
            maxLength={7}
            className={cn(
              'block w-full rounded-md border px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-primary-500',
              isValidHex && !error
                ? 'border-gray-300'
                : 'border-red-300 focus:ring-red-500'
            )}
            aria-label="Hex color code"
            aria-invalid={!isValidHex || !!error}
          />
        </div>

        {/* Color Preview */}
        <div
          className="h-10 w-10 rounded border-2 border-gray-300"
          style={{ backgroundColor: isValidHex ? localValue : '#ffffff' }}
          aria-label="Color preview"
          role="img"
        />
      </div>

      {/* Preset Swatches */}
      {presets.length > 0 && (
        <div>
          <p className="text-xs text-gray-600 mb-2">Quick colors:</p>
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset}
                type="button"
                onClick={() => handlePresetClick(preset)}
                className={cn(
                  'h-8 w-8 rounded border-2 transition-all',
                  'hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500',
                  preset === localValue
                    ? 'border-gray-900 ring-2 ring-gray-900'
                    : 'border-gray-300'
                )}
                style={{ backgroundColor: preset }}
                aria-label={`Select color ${preset}`}
                title={preset}
              />
            ))}
          </div>
        </div>
      )}

      {/* Error Message */}
      {(error || !isValidHex) && (
        <p className="text-sm text-red-600" role="alert">
          {error || 'Invalid hex color format (e.g., #3b82f6)'}
        </p>
      )}
    </div>
  );
}
